import { Box, Button as MuiButton, styled } from "@mui/material";
import { App, View } from "@pimo/pimo-app-builder";
import { TabLayout } from "@pimo/pimo-components";

import { EditOeKpisDialog } from "../../../components/edit-oe-kpis-dialog/edit-oe-kpis-dialog";
import { OeKpiCartCard } from "../../../components/oe-kpi-chart-card/oe-kpi-chart-card";
import OeKpisDataGrid from "../../../components/oe-kpis-data-grid/oe-kpis-data-grid";
import OeKpisTitleCard from "../../../components/oe-kpis-title-card/oe-kpis-title-card";
import { UploadDialog } from "../../../components/upload-dialog/upload-dialog";
import { InAppState } from "../../app";
import { STRAPI_URL } from "../../env";
import { saveOeKpis, uploadOeKpis } from "../../helpers/fetch-helpers";

const Button = styled(MuiButton)({
  borderRadius: "4px",
});

export function buildOeKpisView(
  view: View<InAppState, TabLayout>,
  app: App<InAppState>
) {
  const viewTitle = view.addComponent({
    component: OeKpisTitleCard,
    layoutProps: {
      xs: 12,
      viewname: "oeKpis",
    },
  });

  viewTitle.mapState(({ currentOE }) => ({
    title: `OE KPIs ${currentOE?.operatingEntity ? "- " + currentOE.operatingEntity : ""}`,

    cardProps: { sx: { height: "100%" } },
    lastUpdatedAt: currentOE?.lastUpdatedKpis,
    endSlot: (
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Button
          variant="outlined"
          onClick={() => {
            const oe = app.getAppState().currentOE;

            if (!oe) {
              return;
            }

            window.open(`${STRAPI_URL}/api/bff-oe-kpis/${oe.id}/export`);
          }}
        >
          Export Datasheet
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            app.patchAppState({
              showUploadOeKpisPopup: true,
            });
          }}
        >
          Upload KPIs
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            const oe = app.getAppState().currentOE;

            if (!oe) {
              return;
            }

            app.patchAppState({
              editOeKpisPopup: { content: oe, showPopup: true },
            });
          }}
        >
          Edit KPIs
        </Button>
      </Box>
    ),
  }));

  const dataGrid = view.addComponent({
    component: OeKpisDataGrid,
    layoutProps: {
      xs: 12,
      viewname: "oeKpis",
    },
  });

  dataGrid.mapState(({ currentOE }) => ({
    operatingEntity: currentOE,
    editable: false,
  }));

  const chartCard = view.addComponent({
    component: OeKpiCartCard,
    layoutProps: { xs: 12, viewname: "oeKpis" },
  });

  chartCard.mapState(({ currentOE }) => {
    return {
      operatingEntity: currentOE,
    };
  });

  const uploadPopup = view.addComponent({
    component: UploadDialog,
    layoutProps: { xs: 12, viewname: "oeKpis" },
  });

  uploadPopup.mapState(({ showUploadOeKpisPopup }) => {
    return {
      isOpen: showUploadOeKpisPopup ?? false,
      fileType: "excel",
      hideCommentField: true,
    };
  });

  uploadPopup.on("upload-dialog:close", () =>
    app.patchAppState({ showUploadOeKpisPopup: false })
  );

  uploadPopup.on("upload-dialog:upload-file", async ({ payload }) => {
    const currentOE = app.getAppState().currentOE;
    const file = payload?.file;

    const savedOe = await uploadOeKpis(file, currentOE?.id);

    if (savedOe) {
      app.patchAppState({
        currentOE: savedOe,
        showUploadOeKpisPopup: false,
        snackbar: {
          message: "Upload successful",
          severity: "success",
          open: true,
          type: "snackbar",
        },
      });
    } else {
      app.patchAppState({
        snackbar: {
          message: "An error occured while uploading",
          severity: "error",
          open: true,
          type: "alert",
        },
      });
    }
  });

  const editPopup = view.addComponent({
    component: EditOeKpisDialog,
    layoutProps: { xs: 12, viewname: "oeKpis" },
  });

  editPopup.mapState(({ editOeKpisPopup }) => {
    return {
      open: editOeKpisPopup?.showPopup ?? false,
      title: `Edit KPIs ${editOeKpisPopup?.content?.operatingEntity ? "- " + editOeKpisPopup.content.operatingEntity : ""}`,
      operatingEntity: editOeKpisPopup?.content ?? undefined,
    };
  });

  editPopup.on("popup:save", async ({ payload }) => {
    const oeId = app.getAppState().editOeKpisPopup?.content?.id;

    if (!oeId || !payload) {
      return;
    }
    const savedOe = await saveOeKpis(oeId, payload);

    if (savedOe) {
      app.patchAppState({
        editOeKpisPopup: undefined,
        currentOE: savedOe,
        snackbar: {
          message: "All changes have been saved.",
          severity: "success",
          open: true,
          type: "snackbar",
        },
      });
    } else {
      app.patchAppState({
        snackbar: {
          message: "An error occured while saving",
          severity: "error",
          open: true,
          type: "alert",
        },
      });
    }
  });

  editPopup.on("popup:close", () => {
    app.patchAppState({
      editOeKpisPopup: {
        content: null,
        showPopup: false,
      },
    });
  });
}
